import { BRAND_WORDMARK } from './brand';
/**
 * Template HTML email — Abruzzo.AI (default) e POSA (brand dedicato).
 */

const POSA_GOLD = '#B59A6D';
const POSA_GOLD_DARK = '#9A8158';

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function linkify(escaped: string, linkColor: string): string {
  return escaped.replace(
    /(https?:\/\/[^\s<]+)/g,
    `<a href="$1" style="color: ${linkColor}; text-decoration: underline;" target="_blank" rel="noopener noreferrer">$1</a>`
  );
}

function formatEmailBody(emailBody: string, linkColor: string): string {
  return emailBody
    .split('\n')
    .map((line) => {
      const trimmed = line.trim();
      if (trimmed === '') {
        return '<p style="margin: 0 0 12px 0;"></p>';
      }
      const content = linkify(escapeHtml(trimmed), linkColor);
      return `<p style="margin: 0 0 12px 0; color: #374151; line-height: 1.7; font-size: 15px;">${content}</p>`;
    })
    .join('');
}

/**
 * Dominio pubblico su cui gli asset sono davvero raggiungibili.
 * Serve in sviluppo: un URL localhost dentro un'email e' un'immagine rotta
 * per chi la riceve.
 */
const PUBLIC_APP_URL = (import.meta.env?.VITE_PUBLIC_APP_URL as string | undefined)
  ?.replace(/\/+$/, '') || '';

function absolutePublicUrl(path: string, localhostFallback?: string): string {
  const fallback = localhostFallback
    ?? (PUBLIC_APP_URL ? `${PUBLIC_APP_URL}${path}` : undefined);
  if (typeof window !== 'undefined') {
    const origin = window.location.origin;
    if (origin.includes('localhost') || origin.includes('127.0.0.1')) {
      return fallback ?? `${origin}${path}`;
    }
    return `${origin}${path}`;
  }
  return fallback ?? path;
}

/**
 * Genera template HTML email con colori AbruzzoAI
 */
export function generateEmailHTML(
  emailBody: string,
  _customerName?: string,
  _priceListName?: string
): string {
  const formattedBody = formatEmailBody(emailBody, '#2563eb');

  const logoUrl = absolutePublicUrl(BRAND_WORDMARK);

  return `<!DOCTYPE html>
<html lang="it">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Abruzzo.AI</title>
</head>
<body style="margin: 0; padding: 0; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f3f4f6;">
  <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color: #f3f4f6; padding: 40px 20px;">
    <tr>
      <td align="center">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="600" style="background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1); max-width: 600px;">
          <!-- Header con logo e barra colorata -->
          <tr>
            <td style="background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); padding: 40px 30px; text-align: center;">
              <img src="${logoUrl}" alt="AbruzzoAI Logo" style="max-width: 220px; height: auto; display: block; margin: 0 auto;" />
            </td>
          </tr>
          
          <!-- Contenuto principale -->
          <tr>
            <td style="padding: 50px 40px;">
              <div style="color: #374151; line-height: 1.7;">
                ${formattedBody}
              </div>
            </td>
          </tr>
          
          <!-- Separatore decorativo -->
          <tr>
            <td>
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
                <tr>
                  <td style="padding: 0 40px;">
                    <div style="height: 3px; background: linear-gradient(90deg, transparent, #2563eb 50%, transparent); border-radius: 2px;"></div>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          
          <!-- Footer -->
          <tr>
            <td style="padding: 35px 40px; background-color: #f9fafb;">
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
                <tr>
                  <td align="center" style="padding: 0;">
                    <p style="margin: 0 0 10px 0; color: #6b7280; font-size: 14px; line-height: 1.6;">
                      <strong style="color: #2563eb; font-size: 15px;">ABRUZZO.AI di Appicciutoli Federica</strong><br/>
                      Via fosso foreste, 8 SC A INT 9 - Montesilvano (PE)<br/>
                      P.IVA: 02465100689<br/>
                      <a href="mailto:info@abruzzo.ai" style="color: #2563eb; text-decoration: none;">info@abruzzo.ai</a> | <a href="https://www.abruzzo.ai" style="color: #2563eb; text-decoration: none;">www.abruzzo.ai</a> | WhatsApp: <a href="https://wa.me/393203566659" style="color: #2563eb; text-decoration: none;">+39 320 356 6659</a>
                    </p>
                    <p style="margin: 20px 0 0 0; color: #9ca3af; font-size: 12px; line-height: 1.5;">
                      Questa email è stata inviata automaticamente dal sistema Pixel CRM.
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

/**
 * Template HTML brandizzato POSA (oro/nero, logo chiaro).
 */
export function generatePosaEmailHTML(emailBody: string): string {
  const formattedBody = formatEmailBody(emailBody, POSA_GOLD_DARK);
  const logoUrl = absolutePublicUrl('/posa-logo.png');

  return `<!DOCTYPE html>
<html lang="it">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>POSA — Configuratore Ambienti</title>
</head>
<body style="margin: 0; padding: 0; font-family: Georgia, 'Times New Roman', Times, serif; background-color: #f5f4f1;">
  <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color: #f5f4f1; padding: 40px 20px;">
    <tr>
      <td align="center">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="600" style="background-color: #ffffff; border-radius: 4px; overflow: hidden; box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06); max-width: 600px; border: 1px solid #e8e4dc;">
          <!-- Header con logo POSA -->
          <tr>
            <td style="background-color: #ffffff; padding: 36px 30px 28px; text-align: center; border-bottom: 1px solid #e8e4dc;">
              <img
                src="${logoUrl}"
                alt="POSA - Configuratore Ambienti logo featuring a gold chevron icon and a stylized diamond O."
                style="max-width: 280px; width: 100%; height: auto; display: block; margin: 0 auto;"
              />
            </td>
          </tr>

          <!-- Accento oro -->
          <tr>
            <td style="height: 3px; background: linear-gradient(90deg, ${POSA_GOLD_DARK}, ${POSA_GOLD}, ${POSA_GOLD_DARK}); font-size: 0; line-height: 0;">&nbsp;</td>
          </tr>

          <!-- Contenuto -->
          <tr>
            <td style="padding: 42px 40px 36px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">
              <div style="color: #374151; line-height: 1.7;">
                ${formattedBody}
              </div>
            </td>
          </tr>

          <!-- Separatore oro -->
          <tr>
            <td>
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
                <tr>
                  <td style="padding: 0 40px;">
                    <div style="height: 1px; background: linear-gradient(90deg, transparent, ${POSA_GOLD} 50%, transparent);"></div>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="padding: 28px 40px 32px; background-color: #faf9f6; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
                <tr>
                  <td align="center" style="padding: 0;">
                    <p style="margin: 0 0 8px 0; color: #6b7280; font-size: 13px; line-height: 1.6;">
                      <strong style="color: ${POSA_GOLD_DARK}; font-size: 14px; letter-spacing: 0.04em;">POSA</strong>
                      <span style="color: #9ca3af;"> · </span>
                      Configuratore Ambienti by Abruzzo.AI
                    </p>
                    <p style="margin: 0; color: #9ca3af; font-size: 12px; line-height: 1.5;">
                      <a href="https://posa.abruzzo.ai" style="color: ${POSA_GOLD_DARK}; text-decoration: none;">posa.abruzzo.ai</a>
                      &nbsp;·&nbsp;
                      <a href="https://www.abruzzo.ai" style="color: ${POSA_GOLD_DARK}; text-decoration: none;">www.abruzzo.ai</a>
                      &nbsp;·&nbsp;
                      <a href="mailto:info@abruzzo.ai" style="color: ${POSA_GOLD_DARK}; text-decoration: none;">info@abruzzo.ai</a>
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

export function generateLeadEmailHTML(emailBody: string, templateKey?: string): string {
  if (templateKey === 'posa') {
    return generatePosaEmailHTML(emailBody);
  }
  return generateEmailHTML(emailBody);
}
